import { useState, useEffect, useCallback } from 'react';
import { useViewerStore, ToolName } from '../stores/viewerStore';
import styles from './ViewportContextMenu.module.css';

interface MenuItem {
  label: string;
  shortcut?: string;
  action: () => void;
  divider?: boolean;
}

interface Props {
  onSelectTool: (tool: ToolName) => void;
  onInvert: () => void;
  onFlipH: () => void;
  onFlipV: () => void;
  onRotateL: () => void;
  onRotateR: () => void;
  onReset: () => void;
  onClear: () => void;
}

export default function ViewportContextMenu({
  onSelectTool, onInvert, onFlipH, onFlipV, onRotateL, onRotateR, onReset, onClear,
}: Props) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const toggleOverlays = useViewerStore((s) => s.toggleOverlays);

  const items: MenuItem[] = [
    { label: 'Window/Level', shortcut: 'W', action: () => onSelectTool('Wwwc') },
    { label: 'Pan', shortcut: 'P', action: () => onSelectTool('Pan') },
    { label: 'Zoom', shortcut: 'Z', action: () => onSelectTool('Zoom') },
    { label: 'Scroll', shortcut: 'S', action: () => onSelectTool('StackScroll') },
    { label: 'Magnify', shortcut: 'M', action: () => onSelectTool('Magnify') },
    { label: '', shortcut: '', action: () => {}, divider: true },
    { label: 'Length', shortcut: 'L', action: () => onSelectTool('Length') },
    { label: 'Angle', shortcut: 'A', action: () => onSelectTool('Angle') },
    { label: 'Probe', action: () => onSelectTool('Probe') },
    { label: 'Rectangle ROI', action: () => onSelectTool('RectangleROI') },
    { label: 'Elliptical ROI', action: () => onSelectTool('EllipticalROI') },
    { label: 'Arrow', action: () => onSelectTool('ArrowAnnotate') },
    { label: '', shortcut: '', action: () => {}, divider: true },
    { label: 'Invert', shortcut: 'I', action: onInvert },
    { label: 'Flip Horizontal', action: onFlipH },
    { label: 'Flip Vertical', action: onFlipV },
    { label: 'Rotate Left', action: onRotateL },
    { label: 'Rotate Right', action: onRotateR },
    { label: '', shortcut: '', action: () => {}, divider: true },
    { label: 'Toggle Overlays', shortcut: 'O', action: toggleOverlays },
    { label: 'Reset', shortcut: 'R', action: onReset },
    { label: 'Clear Annotations', action: onClear },
  ];

  const handleContextMenu = useCallback((e: MouseEvent) => {
    // Only show on viewport elements
    const target = e.target as HTMLElement;
    const viewport = target.closest('[id^="viewport-"]');
    if (!viewport) return;

    e.preventDefault();
    setPos({ x: e.clientX, y: e.clientY });
    setVisible(true);
  }, []);

  const handleClick = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className={styles.menu}
      style={{ left: pos.x, top: pos.y }}
    >
      {items.map((item, idx) =>
        item.divider ? (
          <div key={idx} className={styles.divider} />
        ) : (
          <button
            key={idx}
            className={styles.item}
            onClick={() => { item.action(); setVisible(false); }}
          >
            <span>{item.label}</span>
            {item.shortcut && <span className={styles.shortcut}>{item.shortcut}</span>}
          </button>
        )
      )}
    </div>
  );
}
